import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, ArrowRight, Check, Gift, Cake, GraduationCap, Heart, Star, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";

const ArticleCustomColoringPages = () => {
  const occasions = [
    {
      icon: Cake,
      title: "Birthday Parties",
      prompts: [
        "Birthday cake with [age] candles surrounded by balloons and confetti",
        "[Child's name] as a superhero flying over a birthday party",
        "Birthday unicorn wearing a party hat with presents"
      ]
    },
    {
      icon: GraduationCap,
      title: "Educational",
      prompts: [
        "The letter [A] decorated with [apples, ants, alligators]",
        "Math scene with friendly numbers playing together",
        "Solar system with labeled planets for kids to color"
      ]
    },
    {
      icon: Heart,
      title: "Holidays",
      prompts: [
        "Christmas tree decorated with ornaments and presents",
        "Easter bunny hiding eggs in a garden",
        "Halloween pumpkin patch with friendly ghosts"
      ]
    },
    {
      icon: Gift,
      title: "Special Interests",
      prompts: [
        "[Child's pet type] wearing a crown in a castle",
        "[Favorite sport] scene with animal characters",
        "[Favorite vehicle] racing through fun obstacles"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                Blog
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Try Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">Tutorials</span>
              <span>•</span>
              <span>Jan 2026</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                7 min read
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Custom Coloring Pages: How to Make Them in Seconds
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Want a coloring page that's perfectly tailored to your child's interests, a special occasion, or a unique theme? Learn how to create personalized coloring pages that you won't find anywhere else.
            </p>
          </header>

          {/* Quick Demo CTA */}
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-6 mb-12 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">See It In Action</span>
            </div>
            <p className="text-orange-100 mb-4">
              Watch your ideas become coloring pages instantly. Create your first custom page in under 60 seconds!
            </p>
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-10 px-6 rounded-full bg-white text-orange-600 font-semibold hover:bg-orange-50"
            >
              Create Custom Page →
            </Button>
          </div>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12">
            <h2 className="font-semibold text-slate-800 mb-4">📋 What You'll Learn</h2>
            <ul className="space-y-2 text-slate-600">
              <li><a href="#why-custom" className="hover:text-violet-600">Why Custom Coloring Pages?</a></li>
              <li><a href="#how-to" className="hover:text-violet-600">How to Create Custom Pages</a></li>
              <li><a href="#occasions" className="hover:text-violet-600">Ideas for Every Occasion</a></li>
              <li><a href="#personalization" className="hover:text-violet-600">Personalization Tips</a></li>
              <li><a href="#faq" className="hover:text-violet-600">FAQs</a></li>
            </ul>
          </div>

          {/* Why Custom */}
          <section id="why-custom" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Why Custom Coloring Pages?
            </h2>
            <p className="text-slate-600 mb-6">
              Generic coloring pages are everywhere. But custom pages are special – and kids can tell the difference!
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                { title: "Engagement", desc: "Kids color longer when the subject matches their interests" },
                { title: "Memorable", desc: "A page made just for them becomes a keepsake" },
                { title: "Educational", desc: "Reinforce learning with custom educational themes" },
                { title: "Special Occasions", desc: "Perfect for birthdays, holidays, and celebrations" }
              ].map((item, i) => (
                <div key={i} className="bg-orange-50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <p className="text-slate-700">
                <strong>Real example:</strong> Instead of a generic "princess" coloring page, imagine giving your daughter a page of "Princess [her name] riding a purple dragon to her castle in the clouds." That's the power of custom!
              </p>
            </div>
          </section>

          {/* How To */}
          <section id="how-to" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How to Create Custom Pages (3 Simple Steps)
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  step: 1,
                  title: "Think About What Makes It Special",
                  content: "What does your child love? Their pet? A favorite character type? A specific color? The more personal details you include, the more special the page becomes."
                },
                {
                  step: 2,
                  title: "Write a Descriptive Prompt",
                  content: "Combine your personal details into a descriptive sentence. Example: 'A fluffy golden retriever puppy named Max playing fetch in a park with butterflies' is much better than 'dog playing'."
                },
                {
                  step: 3,
                  title: "Generate and Customize",
                  content: "Put your prompt into ColorWonder, generate the page, and if it's not quite right, tweak your prompt and regenerate. It's that simple!"
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 bg-white rounded-xl border border-slate-100">
                  <span className="w-10 h-10 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </span>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                    <p className="text-slate-600">{item.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Occasions */}
          <section id="occasions" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Custom Page Ideas for Every Occasion
            </h2>
            
            <div className="space-y-6">
              {occasions.map((occasion, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                      <occasion.icon className="w-5 h-5 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">{occasion.title}</h3>
                  </div>
                  
                  <p className="text-slate-600 mb-4">Try these prompt templates:</p>
                  
                  <div className="space-y-2">
                    {occasion.prompts.map((prompt, j) => (
                      <div key={j} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                        <Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 text-sm">{prompt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Personalization Tips */}
          <section id="personalization" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Pro Tips for Personalization
            </h2>
            
            <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl p-6">
              <ul className="space-y-4">
                {[
                  {
                    tip: "Include their name in the prompt",
                    example: "Princess Emma riding a unicorn"
                  },
                  {
                    tip: "Reference their pet",
                    example: "Orange tabby cat named Whiskers exploring a garden"
                  },
                  {
                    tip: "Add their favorite colors",
                    example: "Purple dragon with pink wings (they can make it those colors!)"
                  },
                  {
                    tip: "Combine their interests",
                    example: "Dinosaur playing soccer with robot friends"
                  },
                  {
                    tip: "Include family elements",
                    example: "Three siblings building a treehouse together"
                  }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-slate-800">{item.tip}</span>
                      <p className="text-slate-600 text-sm">Example: "{item.example}"</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Real Examples */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Real Custom Page Examples
            </h2>
            <p className="text-slate-600 mb-6">
              Here are actual prompts parents have used to create memorable coloring pages:
            </p>
            
            <div className="space-y-3">
              {[
                "5-year-old boy astronaut exploring Mars with his teddy bear companion",
                "Ballet dancer practicing in a magical forest with woodland creatures watching",
                "Race car driver (girl) winning a trophy at a rainbow racetrack",
                "Family of four camping under stars with their golden retriever",
                "Grandma and grandson baking cookies in a cozy kitchen",
                "Superhero kid saving baby animals from a tree",
                "Mermaid swimming with sea turtles near a underwater castle"
              ].map((prompt, i) => (
                <div key={i} className="p-4 bg-white rounded-xl border border-slate-100">
                  <p className="text-slate-700">"{prompt}"</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
              <Gift className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Create Your First Custom Page
              </h3>
              <p className="text-violet-100 mb-6 max-w-md mx-auto">
                Turn any idea into a personalized coloring page. It's free to start!
              </p>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
              >
                Start Creating Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Can I include my child's name in the coloring page?",
                  a: "You can include names in prompts! While the AI creates images rather than text, you can describe characters as being named something, or you can add names yourself after printing."
                },
                {
                  q: "How specific can I get with my prompts?",
                  a: "Very specific! The more details you provide, the more unique your page will be. Include colors, settings, actions, and personal elements for best results."
                },
                {
                  q: "What if the result isn't quite right?",
                  a: "Simply tweak your prompt and regenerate. You might try being more specific, changing the action, or adjusting the setting. Each generation only takes about 30 seconds."
                },
                {
                  q: "Can I create custom pages for a classroom?",
                  a: "Yes! Teachers love using custom pages for educational themes. Create pages that match your current lesson plan or feature class-relevant content."
                },
                {
                  q: "Are custom pages harder to create?",
                  a: "Not at all! It's the same simple process – you're just writing a more personalized prompt. It takes the same 30 seconds to generate."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">{faq.q}</h4>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Make Every Page Special
            </h2>
            <p className="text-slate-600 mb-4">
              Custom coloring pages transform a simple activity into something meaningful. Whether it's for a birthday, a learning moment, or just because – personalized pages show kids that creativity can be tailored just for them.
            </p>
            <p className="text-slate-600">
              Ready to make your first custom page? <Link to="/signup" className="text-violet-600 font-semibold hover:underline">Try ColorWonder free</Link> and turn your unique ideas into print-ready coloring pages in seconds!
            </p>
          </section>

          {/* Related Articles */}
          <section className="border-t border-slate-200 pt-12">
            <h3 className="text-xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Related Articles
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/coloring-page-prompts" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">50 Free Coloring Page Prompts</h4>
                <p className="text-slate-500 text-sm">Ready-to-use ideas for inspiration</p>
              </Link>
              <Link to="/blog/create-coloring-pages-with-ai" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">How to Create Coloring Pages with AI</h4>
                <p className="text-slate-500 text-sm">Complete beginner tutorial</p>
              </Link>
            </div>
          </section>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-slate-500 text-sm">
          <p>© 2026 ColorWonder. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticleCustomColoringPages;
